import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ApiService, SessionService, CartStateService } from '../../services';
import { HttpResponse } from '@angular/common/http';
import {
  Order,
  OrderStatus,
  PaymentMethod,
  TransactionStatus,
} from '../../models';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { Ticket } from '../../models/tickets';
import { environment } from '@env/environment';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Microservice } from '@hypenation/shared';
import { timer, forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'hypenation-tickets-order-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzButtonModule,
    NzSkeletonModule,
    NzTagModule,
    NzIconModule,
  ],
  templateUrl: './order-confirmation.component.html',
  styleUrl: './order-confirmation.component.css',
})
export class OrderConfirmationComponent implements OnInit {
  orderNumber!: string;
  order!: Order;
  tickets!: Ticket[];
  loadingOrder = false;
  loadingTickets = false;
  apiUrl: string = environment.apiUrl;
  transactionStatusEnum = TransactionStatus;
  orderStatusEnum = OrderStatus;
  microservice = Microservice;

  /**
   * A wallet top-up reaches this page through the same gateway return as a checkout, but it has no
   * cart and issues no tickets. Without this the page told a customer who had just funded a ticket
   * to "check your email for your tickets", and one whose top-up was still pending to "visit your
   * cart to complete the order" — a cart that does not exist and cannot help them.
   */
  get isWalletTopUp(): boolean {
    return this.order?.purpose === 'WALLET_TOP_UP';
  }

  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _apiService: ApiService,
    private readonly _router: Router,
    private readonly _sessionService: SessionService,
    private readonly _cartStateService: CartStateService
  ) {}

  ngOnInit(): void {
    this.orderNumber = this._activatedRoute.snapshot.params['orderNumber'];
    if (this.orderNumber) {
      this.startPolling();
    }

    this.scrollToTop();
  }

  private startPolling(): void {
    timer(0, 10000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          if (!this.order) {
            this.loadingOrder = true;
          }
          if (!this.tickets) {
            this.loadingTickets = true;
          }

          return forkJoin({
            order: this._apiService
              .getOrdersByOrderNumber(this.orderNumber)
              .pipe(catchError(() => of(null))),
            tickets: this._apiService
              .getTicketByOrderNumber(this.orderNumber)
              .pipe(catchError(() => of(null))),
          });
        })
      )
      .subscribe({
        next: (res) => {
          if (res.order?.body) {
            this.order = res.order.body;
            this.handleTerminalOrderState(this.order);
          }
          if (res.tickets?.body) {
            this.tickets = res.tickets.body;
          }
          this.loadingOrder = false;
          this.loadingTickets = false;
        },
        error: () => {
          this.loadingOrder = false;
          this.loadingTickets = false;
        },
      });
  }

  /**
   * Once the order reaches a terminal state, rotate the session so the
   * next order cycle starts with a fresh cart. Rotation drops three
   * things: the pending-order pointer, the cached cart (in-memory +
   * persisted), and the session ID itself. We rotate on every terminal
   * status — not only COMPLETED — because a fresh session yields a new
   * empty cart from the backend, which is the correct starting point
   * for a retry after CANCELLED or CLOSED just as much as after a
   * successful purchase.
   *
   * This is a happy-path optimization; the pending-order boot guard in
   * SessionService runs the same rotation on app startup for users who
   * leave this page before polling observes the terminal state.
   */
  private handleTerminalOrderState(order: Order): void {
    const isTerminal =
      order.status === OrderStatus.COMPLETED ||
      order.status === OrderStatus.CANCELLED ||
      order.status === OrderStatus.CLOSED;

    if (!isTerminal) return;

    this._sessionService.clearPendingOrder();
    this._cartStateService.clearCart();
    this._sessionService.clearSessionId();
  }

  public scrollToTop(): void {
    this._apiService.scrollToTop();
  }

  public addOnsText(ticket: Ticket): string {
    return String(ticket.ticketDetails?.['addOnsText'] ?? '');
  }

  public hasSelectedAddOns(ticket: Ticket): boolean {
    return !!this.addOnsText(ticket);
  }

  public returnToShop(): void {
    // A top-up started from a ticket, so the storefront is not where the customer wants to land.
    this._router.navigateByUrl(this.isWalletTopUp ? '/my-tickets' : '/');
  }
}
